// src/datablog/BlogPost.js
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useParams, Link } from 'react-router-dom';
import { posts, getSortedPosts } from './posts/posts';
import './DataBlog.css';
import { Helmet } from 'react-helmet-async';

const BlogPost = () => {
  const { slug } = useParams();
  const post = posts[slug];
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    if (!post) return;

    const allPosts = getSortedPosts();

    const filtered = allPosts
      .filter(p => p.slug !== post.slug)
      .filter(p => {
        if (post.categories && p.categories) {
          for (const cat of post.categories) {
            if (p.categories.includes(cat)) return true;
          }
        }

        if (post.tags && p.tags) {
          for (const tag of post.tags) {
            if (p.tags.includes(tag)) return true;
          }
        }

        return false;
      })
      .slice(0, 3);

    setRelatedPosts(filtered);
  }, [post, slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (!post) {
    return (
      <div className="blog-post-container">
        <Helmet>
          <title>Post Not Found | Chart2Video Blog</title>
          <meta name="description" content="The blog post you're looking for could not be found." />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="blog-post-not-found">
          <h1>Post Not Found</h1>
          <p>The blog post you're looking for doesn't exist or has been moved.</p>
          <Link to="/datablog" className="blog-post-back">
            &larr; Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const structuredDataString = JSON.stringify(post.structuredData || {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "image": `${window.location.origin}${post.image}`,
    "datePublished": post.date,
    "dateModified": post.updatedDate || post.date,
    "author": {
      "@type": "Organization",
      "name": post.author || "Chart2Video Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chart2Video",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "description": post.summary,
    "keywords": post.tags ? post.tags.join(", ") : "",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": post.canonicalUrl || window.location.href
    }
  });

  return (
    <div className="blog-post-container">
      <Helmet>
        <title>{post.metaTitle || `${post.title} | Chart2Video`}</title>
        <meta name="description" content={post.metaDescription || post.summary} />

        <meta property="og:title" content={post.metaTitle || post.title} />
        <meta property="og:description" content={post.metaDescription || post.summary} />
        <meta property="og:image" content={`${window.location.origin}${post.image}`} />
        <meta property="og:url" content={post.canonicalUrl || window.location.href} />
        <meta property="og:type" content="article" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle || post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.summary} />
        <meta name="twitter:image" content={`${window.location.origin}${post.image}`} />

        <meta property="article:published_time" content={post.date} />
        {post.updatedDate && <meta property="article:modified_time" content={post.updatedDate} />}
        <meta property="article:author" content={post.author || "Chart2Video Team"} />
        {post.tags && post.tags.map(tag => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}

        <link rel="canonical" href={post.canonicalUrl || window.location.href} />

        <script type="application/ld+json">
          {structuredDataString}
        </script>
      </Helmet>

      <div className="blog-post-header">
        <Link to="/datablog" className="blog-post-back">
          &larr; Back to Blog
        </Link>
        <h1 className="blog-post-title">{post.title}</h1>
        <div className="blog-post-meta">
          <span className="blog-post-date">Published on {formatDate(post.date)}</span>
        </div>
        <div className="blog-post-category-tag">
          {post.categories?.map((category, index) => (
            <span key={index} className="blog-post-category">{category}</span>
          ))}
        </div>
      </div>

      {post.image && (
        <div className="blog-post-featured-image">
          <img src={post.image} alt={post.title} />
        </div>
      )}

      <div className="blog-post-content">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{post.content}</ReactMarkdown>
      </div>

      {post.tags && (
        <div className="blog-post-tags-section">
          <h3>Tags</h3>
          <div className="blog-post-tags">
            {post.tags.map((tag, index) => (
              <Link key={index} to="/datablog" className="blog-post-tag">{tag}</Link>
            ))}
          </div>
        </div>
      )}

      {relatedPosts.length > 0 && (
        <div className="blog-post-related">
          <h3>Related Posts</h3>
          <div className="blog-post-related-grid">
            {relatedPosts.map(rp => (
              <Link key={rp.slug} to={`/datablog/${rp.slug}`} className="blog-post-related-link">
                <div className="blog-post-related-image" style={{ backgroundImage: `url(${rp.image})` }}></div>
                <h4>{rp.title}</h4>
                <p>{rp.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="back-to-top-button">
        Back to Top
      </button>
    </div>
  );
};

export default BlogPost;